import React, { useState, useEffect, useMemo, useRef } from "react";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import { Alert, IconButton } from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import PercentIcon from "@mui/icons-material/Percent";
import { motion } from "framer-motion";
import { AnimatedStars } from "../../ScoreAnimation/AnimatedStars";


const LeftCompo = ({
  apiResponseMiddle,
  socketres,
  setCanExplode,
  selectedStudent,
  students,
  isdemo = false,
  passageId,
  alreadyChecked = [],
}) => {
  const [showNumericScore, setShowNumericScore] = useState(true);
  const { hasError, score, selectedStudentData } = useMemo(() => {
    const socketData =
      socketres?.submitted_assignment?.student === selectedStudent
        ? socketres.submitted_assignment
        : socketres?.student === selectedStudent
          ? socketres
          : null;

    if (socketData) {
      return {
        hasError: false,
        score: socketData.score || 0,
        selectedStudentData: null,
      };
    }

    if (isdemo) {
      return {
        hasError: apiResponseMiddle && "error" in apiResponseMiddle && apiResponseMiddle.error,
        score: apiResponseMiddle?.score || 0,
        selectedStudentData: null,
      };
    } else {
      const studentData = selectedStudent
        ? students?.find((student) => student.id === selectedStudent)
        : null;
      return {
        hasError: apiResponseMiddle && "error" in apiResponseMiddle && apiResponseMiddle.error,
        score: studentData?.score || 0,
        selectedStudentData: studentData,
      };
    }
  }, [apiResponseMiddle, socketres, selectedStudent, students, isdemo]);

  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  const [celebratedStudents, setCelebratedStudents] = useState(() => {
    const initial = {};
    alreadyChecked.forEach(({ studentId, passageId, celebrated, scored }) => {
      if (!initial[studentId]) initial[studentId] = {};
      initial[studentId][passageId] = {
        celebrated: !!celebrated,
        scored: !!scored,
      };
    });
    return initial;
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const percentage = score > 100 ? 100 : score;

  return (
    <div className="flex justify-end w-full px-4" id="leftCompoContainer">
      <div className="w-auto">
        <div className="relative group flex justify-end">
          {/* Main Card Container */}
          <div className="relative overflow-hidden bg-gradient-to-br from-white via-white to-blue-50/40 rounded-[2rem] p-5 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.05)] border border-slate-100 flex flex-col items-center min-w-[240px] transition-all duration-500 hover:shadow-[0_30px_60px_-12px_rgba(0,0,0,0.08)]">

            
            {/* Decorative Elements */}
            <div className="absolute top-0 right-0 w-24 h-24 bg-blue-500/5 rounded-full -mr-12 -mt-12 blur-2xl group-hover:bg-blue-500/10 transition-colors" />
            <div className="absolute bottom-0 left-0 w-20 h-20 bg-emerald-500/5 rounded-full -ml-10 -mb-10 blur-2xl group-hover:bg-emerald-500/10 transition-colors" />

            {/* Header */}
            <div className="relative mb-4 flex flex-col items-center w-full">
              <div className="flex items-center justify-between w-full px-2 mb-1">
                <div className="w-8" /> {/* Spacer */}
                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">
                  Reading Proficiency
                </span>
                <IconButton 
                  size="small" 
                  onClick={() => setShowNumericScore(!showNumericScore)}
                  className="!bg-white hover:!bg-blue-600 hover:!text-white !text-blue-600 transition-all shadow-md border border-blue-50"
                  sx={{ width: 34, height: 34 }}
                >
                  {showNumericScore ? <StarIcon sx={{ fontSize: 18 }} /> : <PercentIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </div>
              <div className="h-0.5 w-8 bg-blue-500/20 rounded-full" />
            </div>

            {/* Progress Circle Container */}
            <div className="relative w-full flex items-center justify-center min-h-[160px]">

              {hasError ? (
                <div className="flex flex-col items-center text-red-500">
                  <Alert severity="error" icon={false} sx={{ backgroundColor: 'transparent', p: 0 }}>Error</Alert>
                </div>
              ) : (
                <>
                  {showNumericScore ? (
                    <div className="relative w-36 h-36 flex items-center justify-center">
                    {/* Glow Effect for High Scores */}
                    {percentage >= 90 && (
                      <div className="absolute inset-2 bg-emerald-400/20 rounded-full blur-xl animate-pulse" />
                    )}
                    
                    <div className="relative z-10 w-full h-full drop-shadow-[0_8px_16px_rgba(0,0,0,0.04)]">
                      <CircularProgressbar
                        value={percentage}
                        strokeWidth={10}
                        text={
                          <tspan dy="0.1em" className="font-black text-3xl">
                            {Math.floor(score)}
                          </tspan>
                        }
                        styles={buildStyles({
                          pathColor:
                            percentage >= 90
                              ? "#10b981"
                              : percentage >= 70
                                ? "#3b82f6"
                                : percentage >= 40
                                  ? "#f59e0b"
                                  : "#ef4444",
                          textColor: "#1e293b",
                          trailColor: "#f1f5f9",
                          strokeLinecap: "round",
                          pathTransitionDuration: 1.5,
                          textSize: "28px",
                        })}
                      />
                    </div>

                    {/* Score Status Label */}
                    <div className="absolute -bottom-2 bg-white px-3 py-1 rounded-full shadow-sm border border-slate-100 z-20 transition-transform group-hover:scale-105">
                      <span className={`text-[10px] font-bold uppercase tracking-wider ${
                        percentage >= 90 ? "text-emerald-600" : percentage >= 70 ? "text-blue-600" : "text-slate-500"
                      }`}>
                        {percentage === 100 ? "Perfect!" : percentage >= 90 ? "Excellent" : percentage >= 70 ? "Good Job" : "Keep Practicing"}
                      </span>
                    </div>
                  </div>
                  ) : (
                    <div className="flex flex-col items-center py-4">
                       <AnimatedStars value={(score / 100) * 5} starSize={56} />
                       <div className="mt-6 bg-blue-600 text-white px-6 py-2.5 rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg shadow-blue-200">
                          {(score / 100) * 5 >= 4.5 ? "Master" : (score / 100) * 5 >= 3.5 ? "Expert" : (score / 100) * 5 >= 2.5 ? "Advanced" : "Learner"}
                       </div>
                    </div>
                  )}

                </>
              )}
            </div>

            {/* Icon Badge for High Scores */}
            {percentage >= 95 && (
              <div className="mt-4 animate-bounce">
                <div className="bg-amber-100 p-1.5 rounded-full shadow-inner">
                  <svg className="w-5 h-5 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeftCompo;
